"use client";

import { type ReactNode, createContext, useContext, useMemo } from "react";
import type { SectionEventTranslations } from "../types";

const TRANSLATION_KEYS: Array<keyof SectionEventTranslations> = [
  "clear",
  "clearAll",
  "close",
  "country",
  "attendance",
  "eventType",
  "viewMore",
  "location",
  "filters",
  "searchPlaceholder",
  "noEvents",
  "sortNewest",
  "sortOldest",
];

type TranslationKey = (typeof TRANSLATION_KEYS)[number];

const DEFAULT_TRANSLATIONS: Record<TranslationKey, string> = Object.fromEntries(
  TRANSLATION_KEYS.map(key => [key, `t.${key}`]),
) as Record<TranslationKey, string>;

interface SectionEventTranslationContextValue {
  t: SectionEventTranslations;
}

const SectionEventTranslationContext =
  createContext<SectionEventTranslationContextValue | null>(null);

interface SectionEventTranslationProviderProps {
  blok: Partial<SectionEventTranslations>;
  children: ReactNode;
}

export function SectionEventTranslationProvider({
  blok,
  children,
}: Readonly<SectionEventTranslationProviderProps>) {
  const t = useMemo(
    () => ({
      ...DEFAULT_TRANSLATIONS,
      ...blok,
    }),
    [blok],
  );

  return (
    <SectionEventTranslationContext.Provider value={{ t }}>
      {children}
    </SectionEventTranslationContext.Provider>
  );
}

export function useSectionEventTranslations() {
  const context = useContext(SectionEventTranslationContext);
  if (!context) {
    throw new Error(
      "useSectionEventTranslations must be used within a SectionEventTranslationProvider",
    );
  }
  return context;
}
